import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { getUserBadges, getAllBadges, updateUser, uploadProfilePhoto, getFileUrl } from '../lib/api';
import { API_URL } from '../config/api';
import { safeSetItem } from '../utils/storageUtils';
import { isNative } from '../lib/native';
import axios from 'axios';
import { toast } from 'sonner';
import httpClient from '../lib/http';
import { 
  Settings, ChevronRight, LogOut, Scale, Flame, Trophy, Calendar,
  Award, Lock, Check, Sun, Moon, Monitor, Camera, X, Star, TrendingDown,
  Dumbbell, Medal, Target, Crown, PenLine, BookOpen, Users, Heart, Zap, Bell, Activity, Mic, MicOff,
  Globe, Sparkles, Rocket, UserX, Trash2, Volume2, VolumeX, MapPin, Stethoscope, Shield, Fingerprint, User
} from 'lucide-react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { OfflinePage } from '../components/OfflineComponents';
import InstallPWAModal from '../components/InstallPWAModal';
import HealthIntegration from '../components/HealthIntegration';
import RemindersSection from '../components/RemindersSection';
import ChangePasswordButton from '../components/ChangePasswordButton';
import ChangeEmailButton from '../components/ChangeEmailButton';
import ChangePhoneButton from '../components/ChangePhoneButton';
import ActiveSessionsButton from '../components/ActiveSessionsButton';
import PrivacyProfileButton from '../components/PrivacyProfileButton';
import MessagesPrivacyButton from '../components/MessagesPrivacyButton';
import { usePremiumStatus } from '../lib/queries';
import { getCountryList, getCountryName } from '../utils/countries';
import { countryCodeToFlag } from '../utils/countryFlag';
import BugReportButton from '../components/BugReportButton';
import SupportButton from '../components/SupportButton';

// Badge icon mapping
const BADGE_ICONS = {
  Scale, Flame, Calendar, Trophy, Star, TrendingDown, Dumbbell, Medal,
  Award, Target, Crown, PenLine, BookOpen, Users, Heart, Zap, Sparkles, Rocket
};

// Get cached profile data synchronously
const getCachedProfile = () => {
  try {
    const cached = localStorage.getItem('profile_cache');
    if (cached) {
      const { userBadges, allBadges, premiumStatus, timestamp } = JSON.parse(cached);
      // Cache valid for 2 minutes
      if (userBadges && Date.now() - timestamp < 2 * 60 * 1000) {
        return { userBadges, allBadges, premiumStatus };
      }
    }
  } catch (e) {
    console.error('[Profilo] Error:', e.message);
  }
  return null;
};

export default function Profilo() {
  const { user, logout, refreshUser } = useAuth();
  const { isDark, theme, setTheme } = useTheme();
  const { t, language, setLanguage, languages, currentLanguage } = useLanguage();
  const { formatNumber, convertWeight, convertHeight, weightUnit, heightUnit } = useUnits();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  
  // Initialize from cache IMMEDIATELY
  const cachedData = getCachedProfile();
  const [userBadges, setUserBadges] = useState(cachedData?.userBadges || []);
  const [allBadges, setAllBadges] = useState(cachedData?.allBadges || []);
  const [loading, setLoading] = useState(!cachedData);
  const [networkError, setNetworkError] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showNameBioModal, setShowNameBioModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [savingCountry, setSavingCountry] = useState(false);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(true);
  const [pushToggleVisible, setPushToggleVisible] = useState(true);
  // Biometric (Face ID / Touch ID) — only relevant on native iOS/Android
  const [biometric, setBiometric] = useState({
    supported: false,
    enabled: false,
    label: 'Biometria',
  });
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [isAndroidDevice, setIsAndroidDevice] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(cachedData?.premiumStatus || null);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [applyingReferral, setApplyingReferral] = useState(false);
  const [referralMessage, setReferralMessage] = useState({ type: '', text: '' });
  const [videoMuted, setVideoMuted] = useState(() => localStorage.getItem('slimmy_video_muted') !== 'false');
  const [showWorkoutMap, setShowWorkoutMap] = useState(() => localStorage.getItem('slimmy_workout_map') !== 'false');
  // Weight visibility in challenges — defaults to true; refreshed from server on mount.
  // Cached in localStorage so the toggle shows the correct state instantly.
  const [showWeightInChallenges, setShowWeightInChallenges] = useState(() => localStorage.getItem('slimmy_show_weight') !== 'false');
  // Walk privacy — when true, starting a walk does NOT push a
  // "workout started" notification to the user's challenge peers.
  // Local optimistic state mirrors the server field `walk_privacy_no_notify`.
  const [walkPrivacyNoNotify, setWalkPrivacyNoNotify] = useState(() => localStorage.getItem('slimmy_walk_privacy') === 'true');
  // GLP-1 support — when true, Chef AI adapts recipes for users on
  // GLP-1 weight-management therapies. Local optimistic state mirrors
  // the server field `glp1_support`.
  const [glp1Support, setGlp1Support] = useState(() => localStorage.getItem('slimmy_glp1_support') === 'true');
  // Microphone permission state — 'prompt' | 'granted' | 'denied' | 'unsupported'.
  // We poll on mount + on visibilitychange so returning from iOS Settings
  // (where the user could revoke/grant mic access) refreshes the toggle
  // and the Walkie-Talkie button availability in Camminata.js.
  const [micPermission, setMicPermission] = useState('prompt');

  // Premium status — centralized React Query hook (shared across Dashboard, MealPlan, Premium, Profilo).
  const { data: premiumFromQuery } = usePremiumStatus();
  // Keep local state for seamless cache/rehydration of other fields (userBadges, allBadges)
  // but prefer the query data when available.
  const effectivePremium = premiumFromQuery || premiumStatus;

  const loadData = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      setNetworkError(false);
      const [userBadgesRes, allBadgesRes] = await Promise.all([
        getUserBadges().catch(() => ({ data: [] })),
        getAllBadges().catch(() => ({ data: [] })),
      ]);
      setUserBadges(userBadgesRes.data || []);
      setAllBadges(allBadgesRes.data || []);
      
      // Save to cache with quota management
      safeSetItem('profile_cache', {
        userBadges: userBadgesRes.data || [],
        allBadges: allBadgesRes.data || [],
        premiumStatus: premiumFromQuery || null,
        timestamp: Date.now()
      });
    } catch (err) {
      console.error('Profile error:', err);
    } finally {
      setLoading(false);
    }
  }, [premiumFromQuery]);

  useEffect(() => {
    // Load fresh data in background (cache is already showing)
    loadData(false);
  }, [loadData]);

  // Fetch weight-privacy setting from server on mount — keep the toggle
  // state in sync across devices. Cached in localStorage for instant UI.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await httpClient.get(`${API_URL}/api/users/me/privacy-settings`);
        if (cancelled) return;
        const serverVal = data?.show_weight !== false;
        setShowWeightInChallenges(serverVal);
        localStorage.setItem('slimmy_show_weight', String(serverVal));
      } catch { /* fail silent — keep cached value */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch walk-privacy setting from /auth/me (source of truth for
  // `walk_privacy_no_notify`). Falls back to the cached localStorage
  // value if the request fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await httpClient.get(`${API_URL}/api/auth/me`);
        if (cancelled || !data) return;
        const serverVal = !!data.walk_privacy_no_notify;
        setWalkPrivacyNoNotify(serverVal);
        localStorage.setItem('slimmy_walk_privacy', String(serverVal));
        const glp1Val = !!data.glp1_support;
        setGlp1Support(glp1Val);
        localStorage.setItem('slimmy_glp1_support', String(glp1Val));
      } catch { /* fail silent — keep cached value */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll microphone permission state on mount + on visibilitychange
  // (the user may have gone to iOS Settings to grant/revoke). Also
  // broadcasts to Camminata via localStorage so the walkie-talkie
  // button can enable/disable itself accordingly.
  useEffect(() => {
    let cancelled = false;
    const readPerm = async () => {
      try {
        if (!navigator.permissions?.query) {
          if (!cancelled) setMicPermission('unsupported');
          return;
        }
        const status = await navigator.permissions.query({ name: 'microphone' });
        if (cancelled) return;
        setMicPermission(status.state);
        localStorage.setItem('slimmy_mic_permission', status.state);
        // React on live changes (works on Chromium, best-effort on WebKit).
        status.onchange = () => {
          setMicPermission(status.state);
          localStorage.setItem('slimmy_mic_permission', status.state);
        };
      } catch {
        if (!cancelled) setMicPermission('unsupported');
      }
    };
    readPerm();
    const onVis = () => { if (document.visibilityState === 'visible') readPerm(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Hide bottom nav when edit modal is open to prevent touch offset issues
  useEffect(() => {
    if (showEditModal) {
      document.body.classList.add('modal-open-hide-nav');
    } else {
      document.body.classList.remove('modal-open-hide-nav');
    }
    return () => {
      document.body.classList.remove('modal-open-hide-nav');
    };
  }, [showEditModal]);

  // Check push notification status
  useEffect(() => {
    const checkPushStatus = async () => {
      // Admin can hide the push toggle globally — fetch the flag from
      // public settings and keep the user profile clean if disabled.
      try {
        const { data: publicSettings } = await axios.get(`${API_URL}/api/settings/public`);
        if (publicSettings && publicSettings.push_toggle_enabled === false) {
          setPushToggleVisible(false);
        }
      } catch (_) { /* keep default true on error */ }

      // Check device type and PWA install capability
      const { isAndroid, canInstallPWA, isInstalledPWA, isWrappedWebView } = await import('../lib/pwa');
      const { Capacitor } = await import('@capacitor/core');
      setIsAndroidDevice(isAndroid());
      setCanInstall(canInstallPWA());

      // Check if running on native platform (ios or android via Capacitor)
      const nativePlatform = Capacitor.getPlatform();
      const isOnNativePlatform = nativePlatform === 'ios' || nativePlatform === 'android';

      // Custom WebView wrappers like Natively (iOS IPA or Android APK)
      // expose web APIs but NOT the Capacitor bridge. Treat them as
      // fully installed apps so the toggle shows correctly instead of
      // the misleading "Install PWA" prompt.
      const isWrapped = isWrappedWebView();

      // On native app, push is always supported via Capacitor
      if (isOnNativePlatform) {
        console.log('[Push] Native platform detected:', nativePlatform);
        setPushSupported(true);
        // Check current push status from server
        try {
          const sessionToken = localStorage.getItem('session_token');
          const res = await fetch(`${API_URL}/api/notifications/push-status`, {
            credentials: 'include',
            headers: sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}
          });
          if (res.ok) {
            const data = await res.json();
            setPushEnabled(data.enabled);
          }
        } catch (err) {
          console.log('[Push] Failed to get status:', err);
        }
        setPushLoading(false);
        return;
      }
      
      // If already installed as PWA, push should be supported
      if (isInstalledPWA() || isWrapped) {
        if (isWrapped) {
          console.log('[Push] Native WebView wrapper (Natively/TWA) detected');
        }
        setPushSupported(true);
      } else {
        // Check if push is supported on this browser
        try {
          const { isPushSupported } = await import('../lib/firebase');
          const supported = isPushSupported();
          setPushSupported(supported);
          
          if (!supported) {
            return;
          }
        } catch (err) {
          setPushSupported(false);
          return;
        }
      }
      
      try {
        const sessionToken = localStorage.getItem('session_token');
        const authHeader = sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
        const res = await fetch(`${API_URL}/api/notifications/push-status`, {
          credentials: 'include',
          headers: authHeader
        });
        if (res.ok) {
          const data = await res.json();
          setPushEnabled(data.enabled);

          // If localStorage has a player_id but backend doesn't, re-register it.
          const localPlayer = localStorage.getItem('onesignal_player_id');
          if (localPlayer && !data.enabled) {
            try {
              await fetch(`${API_URL}/api/notifications/onesignal-player`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader },
                credentials: 'include',
                body: JSON.stringify({ player_id: localPlayer }),
              });
              setPushEnabled(true);
            } catch (err) {
              console.error('Failed to re-register OneSignal player:', err);
            }
          }
          // Legacy: clear stale fcm_token from previous Firebase implementation
          if (localStorage.getItem('fcm_token')) {
            localStorage.removeItem('fcm_token');
          }
        }
      } catch (err) {
        console.error('Push status check error:', err);
      }
    };
    checkPushStatus();
    
    // Re-check canInstall periodically (the prompt might become available)
    const interval = setInterval(async () => {
      const { canInstallPWA } = await import('../lib/pwa');
      setCanInstall(canInstallPWA());
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);

  // Biometric login (Face ID / Touch ID) state — detected on mount.
  // On web/PWA `supported` stays false so the toggle is hidden.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const bio = await import('../lib/biometric');
        const avail = await bio.checkBiometryAvailability();
        const enabled = await bio.isBiometricLoginEnabled();
        if (mounted) {
          setBiometric({
            supported: !!avail.available,
            enabled,
            label: avail.label || 'Biometria',
          });
        }
      } catch (_) { /* not native or plugin missing — leave defaults */ }
    })();
    return () => { mounted = false; };
  }, []);

  const handleToggleBiometric = async () => {
    setBiometricBusy(true);
    try {
      const bio = await import('../lib/biometric');
      if (biometric.enabled) {
        await bio.disableBiometricLogin();
        setBiometric((b) => ({ ...b, enabled: false }));
        toast.success(t('profile.biometricDisabled', { type: biometric.label }));
      } else {
        const res = await bio.enableBiometricLogin();
        if (res.success) {
          setBiometric((b) => ({ ...b, enabled: true }));
          toast.success(t('profile.biometricEnabled', { type: biometric.label }));
        } else {
          // Surface the real error so we can diagnose "scan succeeds
          // but toggle stays off" cases. `userCancel` is the only one
          // we treat silently (user said no).
          if (res.error === 'userCancel' || res.error === 'systemCancel' || res.error === 'appCancel') {
            // user cancelled — no error toast
          } else {
            toast.error(`${t('profile.biometricEnableFailed')} [${res.error}${res.detail ? ': ' + res.detail.slice(0, 100) : ''}]`);
            console.error('[biometric] enable failed', res);
          }
        }
      }
    } catch (e) {
      console.error('[biometric] toggle exception', e);
      toast.error(`${t('profile.biometricEnableFailed')} [exception: ${String(e?.message || e).slice(0, 100)}]`);
    } finally {
      setBiometricBusy(false);
    }
  };

  // Lock body scroll when Health modal is open
  useEffect(() => {
    if (showHealthModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showHealthModal]);

  const handleTogglePush = async () => {
    if (!pushSupported) {
      // Check if we can install PWA directly (Android)
      if (canInstall) {
        setPushLoading(true);
        try {
          const { installPWA } = await import('../lib/pwa');
          const result = await installPWA();
          if (result.success) {
            // PWA installed, reload to get push support
            window.location.reload();
          }
        } catch (err) {
          console.error('PWA install error:', err);
        } finally {
          setPushLoading(false);
        }
        return;
      }
      
      // Show custom install PWA modal (iOS)
      setShowInstallModal(true);
      return;
    }
    
    setPushLoading(true);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const authHeaders = sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
      
      if (pushEnabled) {
        // Disable notifications — delete OneSignal player for this user
        const playerId = localStorage.getItem('onesignal_player_id');
        await fetch(`${API_URL}/api/notifications/onesignal-player`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          credentials: 'include',
          body: JSON.stringify(playerId ? { player_id: playerId } : {}),
        });
        localStorage.removeItem('onesignal_player_id');
        localStorage.removeItem('fcm_token');  // legacy cleanup
        setPushEnabled(false);
      } else {
        // Check if on native platform
        const { Capacitor } = await import('@capacitor/core');
        const nativePlatform = Capacitor.getPlatform();
        const isOnNativePlatform = nativePlatform === 'ios' || nativePlatform === 'android';

        // OneSignal unified flow — works for Capacitor native AND web PWA.
        // The wrapper takes care of bridging to the right SDK underneath.
        try {
          const {
            initOneSignal,
            requestOneSignalPermission,
            getOneSignalPlayerId,
            loginOneSignalUser,
            registerOneSignalPlayerOnBackend,
          } = await import('../lib/onesignal');

          await initOneSignal();

          // Tag this subscription with the Slimmy user id so admin can
          // target by externalUserId / user_id segment.
          if (user?.user_id) {
            try { await loginOneSignalUser(user.user_id); } catch { /* non-fatal */ }
          }

          const granted = await requestOneSignalPermission();
          if (!granted) {
            const denyMsg = isOnNativePlatform
              ? (t('profile.pushDenied') || 'Hai negato il permesso per le notifiche. Vai nelle impostazioni iPhone → Slimmy → Notifiche per abilitarle.')
              : 'Hai negato il permesso per le notifiche. Vai nelle impostazioni del browser per abilitarle.';
            alert(denyMsg);
            return;
          }

          // Poll for the player_id — on iOS APNs registration can take
          // a moment after permission is granted.
          let playerId = null;
          for (let i = 0; i < 8; i += 1) {
            playerId = await getOneSignalPlayerId();
            if (playerId) break;
            await new Promise((r) => setTimeout(r, 1000));
          }

          if (!playerId) {
            alert(
              'Notifiche autorizzate ma OneSignal non ha ancora restituito un player_id. ' +
              'Chiudi e riapri l\'app, poi riprova dal Profilo.'
            );
            return;
          }

          const ok = await registerOneSignalPlayerOnBackend(
            playerId,
            isOnNativePlatform ? nativePlatform : 'web',
          );
          if (!ok) {
            alert('Errore nel salvataggio del player_id sul backend.');
            return;
          }
          localStorage.setItem('onesignal_player_id', playerId);
          setPushEnabled(true);
        } catch (err) {
          console.error('OneSignal toggle error:', err);
          alert('Errore notifiche: ' + (err?.message || 'sconosciuto'));
        }
      }
    } catch (err) {
      console.error('Push toggle error:', err);
      alert('Errore nella gestione delle notifiche');
    } finally {
      setPushLoading(false);
    }
  };


  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Show offline page if network error
  if ((!isOnline || networkError) && allBadges.length === 0) {
    return <OfflinePage onRetry={loadData} />;
  }

  // Show loading only on first load (no cache) — wait for ALL data
  if (loading && allBadges.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`}>
        <div className="spinner" />
      </div>
    );
  }

  const earnedCodes = new Set(userBadges.map(b => b.badge_code));

  const themeLabel = {
    system: t('profile.themeSystem'),
    light: t('profile.themeLight'),
    dark: t('profile.themeDark')
  };

  // Badge name translation helper
  const getBadgeName = (badge) => {
    const translatedName = t(`badgeNames.${badge.code}`);
    // If translation exists and is different from the key, use it
    if (translatedName && !translatedName.startsWith('badgeNames.')) {
      return translatedName;
    }
    // Fallback to original name from backend
    return badge.name;
  };

  return (
    <div className={`min-h-full ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} 
         data-testid="profilo-page">
      
      {/* Fixed Header */}
      <div 
        className={`fixed top-0 left-0 right-0 z-30 px-4 pb-3 ${isDark ? 'bg-[#080D1A]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        <div className="max-w-md mx-auto w-full flex items-center justify-between">
          <h1 className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('profile.title')}</h1>
          <button 
            onClick={() => setShowAccountSettings(true)}
            data-testid="account-settings-btn"
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDark ? 'bg-white/5' : 'bg-gray-100'
            }`}
          >
            <Settings className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
          </button>
        </div>
      </div>

      {/* Spacer to push content below fixed header */}
      <div style={{ height: 'calc(env(safe-area-inset-top, 0px) + 70px)' }} />

      {/* Content */}
      <div className="pt-4">

        {/* Profile Card */}
        <div className="px-4 mb-6">
        <div className="glass-card p-6 text-center">
          <ProfilePhoto user={user} isDark={isDark} onPhotoUpdate={refreshUser} />
          
          <div className="flex items-center justify-center gap-2 mb-1">
            <h2 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {user?.name}
            </h2>
            <button
              onClick={() => setShowNameBioModal(true)}
              className={`p-1.5 rounded-full transition-colors ${
                isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
              }`}
              data-testid="edit-name-btn"
            >
              <PenLine className="w-4 h-4" />
            </button>
          </div>
          {user?.username && (
            <p className={`text-sm mb-1 ${isDark ? 'text-white/50' : 'text-gray-400'}`} data-testid="profile-username">
              {user.username}
            </p>
          )}
          <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{user?.email}</p>
          
          {user?.bio && (
            <p className={`text-sm mb-4 italic ${isDark ? 'text-white/80' : 'text-gray-700'}`}>"{user.bio}"</p>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <Scale className="w-5 h-5 text-[#16A34A] mx-auto mb-1" />
              <span className={`font-display text-lg block ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {user?.height_cm || '—'}
              </span>
              <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('profile.heightCm')}</span>
            </div>
            <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <Flame className="w-5 h-5 text-[#F59E0B] mx-auto mb-1" />
              <span className={`font-display text-lg block ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {user?.streak_days || 0}
              </span>
              <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('profile.streak')}</span>
            </div>
            <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <Trophy className="w-5 h-5 text-[#E8334A] mx-auto mb-1" />
              <span className={`font-display text-lg block ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {user?.points || 0}
              </span>
              <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('profile.points')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Admin shortcut — visible only to admin users. Sits between
          the profile card and the Premium banner so admins can
          one-tap into the dashboard from their own profile view. */}
      {user?.is_admin && (
        <div className="px-4 mb-3">
          <button
            onClick={() => navigate('/admin')}
            className="w-full glass-card-sm p-4 flex items-center gap-4 text-left"
            data-testid="admin-shortcut-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('profile.adminArea')}
              </p>
              <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('profile.adminAreaSubtitle')}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 opacity-70" />
          </button>
        </div>
      )}

      {/* Diagnostica — admin-only debug page. Placed right below Admin Area
          per UX request; matches the Admin card's coloured-icon style for
          visual pairing (both are admin-only entry points). */}
      {user?.is_admin && (
        <div className="px-4 mb-3">
          <button
            onClick={() => navigate('/app/diagnostica')}
            className="w-full glass-card-sm p-4 flex items-center gap-4 text-left"
            data-testid="diagnostica-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#14B8A6] to-[#0EA5E9] flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Diagnostica
              </p>
              <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('profile.diagnosticaSubtitle')}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 opacity-70" />
          </button>
        </div>
      )}

      {/* Premium Banner */}
      {effectivePremium && (
        <div className="px-4 mb-6">
          {effectivePremium.is_premium ? (
            // Already Premium - show status
            <button
              onClick={() => navigate('/app/premium')}
              className="w-full glass-card-sm p-4 flex items-center gap-4 text-left"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8334A] to-[#F59E0B] flex items-center justify-center flex-shrink-0">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t('profile.premiumActive')}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#E8334A] text-white">PRO</span>
                </div>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {effectivePremium.premium_lifetime 
                    ? t('profile.lifetimeSubscription')
                    : t('profile.expiresOn', { date: new Date(effectivePremium.premium_expires_at).toLocaleDateString() })
                  }
                </p>
              </div>
              <ChevronRight className={isDark ? 'text-white/40' : 'text-gray-400'} size={20} />
            </button>
          ) : (
            // Not Premium - show upgrade CTA
            <button
              onClick={() => navigate('/app/premium')}
              className="w-full p-4 rounded-2xl bg-gradient-to-r from-[#E8334A] to-[#F59E0B] text-white flex items-center gap-4 shadow-lg shadow-[#E8334A]/20"
              data-testid="premium-cta-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Crown className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <span className="font-bold block">{t('profile.goPremium')}</span>
                <span className="text-sm text-white/80">
                  {!effectivePremium.trial_used 
                    ? t('profile.freeTrial', { days: 30 })
                    : t('profile.unlockAll')
                  }
                </span>
              </div>
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      )}

      {/* Referral/promo code input moved to Premium page — see /app/premium */}

      {/* Informazioni Personali — posizionata SOTTO la scheda Premium
          (sopra Badges). Apre EditProfileModal con altezza, sesso, età,
          activity level e weekly goal. */}
      <div className="px-4 mb-6">
        <button 
          onClick={() => setShowEditModal(true)}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="personal-info-btn"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <User className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('profile.personalInfo')}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.personalInfoSubtitle') || 'Altezza, età, sesso, obiettivo'}
              </p>
            </div>
          </span>
          <ChevronRight className="w-5 h-5 opacity-70" />
        </button>
      </div>

      {/* Badges */}
      <div className="px-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-display flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Award className="w-5 h-5 text-[#F59E0B]" />
            {t('profile.badges.title')} ({userBadges.length}/{allBadges.length})
          </h3>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[...allBadges]
            .sort((a, b) => {
              const aEarned = earnedCodes.has(a.code);
              const bEarned = earnedCodes.has(b.code);
              if (aEarned && !bEarned) return -1;
              if (!aEarned && bEarned) return 1;
              return 0;
            })
            .map(badge => {
            const earned = earnedCodes.has(badge.code);
            const IconComponent = BADGE_ICONS[badge.icon] || Award;
            const badgeColor = badge.color || '#F59E0B';
            
            return (
              <div 
                key={badge.code}
                className={`glass-card-sm px-3 py-2 text-center relative overflow-hidden ${
                  earned ? '' : 'opacity-40'
                }`}
              >
                {/* Modern icon badge */}
                <div 
                  className={`w-9 h-9 mx-auto mb-1 rounded-lg flex items-center justify-center ${earned ? '' : 'grayscale'}`}
                  style={!earned ? {} : undefined}
                >
                  <IconComponent 
                    className="w-5 h-5" 
                    strokeWidth={earned ? 2.5 : 2}
                    style={{ color: earned ? badgeColor : undefined, filter: earned ? 'brightness(0.75)' : undefined }}
                  />
                </div>
                <p className={`text-[11px] font-medium leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {getBadgeName(badge)}
                </p>
                <p className={`text-[9px] mt-0.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{badge.points} pt</p>
                {!earned && (
                  <Lock className={`w-3 h-3 mx-auto mt-0.5 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
                )}
                {earned && (
                  <div 
                    className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: badgeColor }}
                  >
                    <Check className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 space-y-2">
        {/* Health Integration — placed ABOVE notifications per UX
            request. Matches the Push Notifications card dimensions
            exactly (p-4, w-5 icon, default text, text-xs subtitle)
            for visual consistency. Red brand background distinguishes
            it as the primary "connect your health data" CTA. */}
        <button
          onClick={() => setShowHealthModal(true)}
          className="w-full p-4 rounded-2xl bg-[#E8334A] flex items-center justify-between shadow-md shadow-[#E8334A]/20 active:scale-[0.98] transition-transform"
          data-testid="health-integration-btn"
        >
          <span className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-white/95 flex items-center justify-center flex-shrink-0">
              <Activity className="w-5 h-5 text-[#E8334A]" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-white font-medium">{t('profile.healthFitness')}</p>
              <p className="text-xs text-white/80 truncate">
                {t('profile.healthFitnessSubtitle')}
              </p>
            </div>
          </span>
          <ChevronRight className="w-5 h-5 text-white/80 flex-shrink-0" />
        </button>

        {/* Biometric login (Face ID / Touch ID / fingerprint) — only
            shown on native iOS/Android where the platform actually
            supports biometry. Hidden on web/PWA and on native devices
            without enrolled biometrics. Placed right after the Health
            CTA per UX request: it's the primary security shortcut. */}
        {biometric.supported && (
          <button
            onClick={handleToggleBiometric}
            disabled={biometricBusy}
            className="w-full glass-card-sm p-4 flex items-center justify-between"
            data-testid="biometric-toggle-btn"
          >
            <span className={`flex items-center gap-3 min-w-0 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Fingerprint className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              <div className="text-left min-w-0">
                <p className="font-medium">{t('profile.biometricLogin', { type: biometric.label })}</p>
                <p className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {biometric.enabled
                    ? t('profile.biometricSubtitleEnabled', { type: biometric.label })
                    : t('profile.biometricSubtitleDisabled', { type: biometric.label })}
                </p>
              </div>
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              {biometricBusy ? (
                <div className="w-5 h-5 border-2 border-t-transparent border-[#E8334A] rounded-full animate-spin" />
              ) : (
                <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                  biometric.enabled ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
                }`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    biometric.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              )}
            </div>
          </button>
        )}

        {/* Push Notifications Toggle — hidden when admin disables it */}
        {pushToggleVisible && (
        <button 
          onClick={handleTogglePush}
          disabled={pushLoading}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="push-notifications-btn"
        >
          <span className={`flex items-center gap-3 min-w-0 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Bell className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            <div className="text-left min-w-0">
              <p className="font-medium">{t('profile.pushNotifications')}</p>
              <p className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.pushNotificationsSubtitle')}
              </p>
            </div>
          </span>
          <div className="flex items-center gap-2">
            {pushLoading ? (
              <div className="w-5 h-5 border-2 border-t-transparent border-[#E8334A] rounded-full animate-spin" />
            ) : !pushSupported ? (
              canInstall ? (
                // Android - can install directly
                <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-[#E8334A] text-white">
                  {t('profile.installApp')}
                </span>
              ) : (
                // iOS - show guide
                <span className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                  {t('profile.installPWA')}
                </span>
              )
            ) : (
              <>
                <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {pushEnabled ? t('profile.pushActive') : t('profile.pushInactive')}
                </span>
                <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                  pushEnabled ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
                }`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    pushEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </>
            )}
          </div>
        </button>
        )}

        {/* Reminders (per-user, timezone-aware) */}
        <RemindersSection />

        {/* Account & security */}
        <ChangeEmailButton />
        <ChangePhoneButton />
        <ChangePasswordButton hasPassword={!!user?.has_password} />
        <ActiveSessionsButton />
        <PrivacyProfileButton />
        <MessagesPrivacyButton />

        <button 
          onClick={() => setShowThemeModal(true)}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {isDark ? <Moon className="w-5 h-5 opacity-70" /> : <Sun className="w-5 h-5 opacity-70" />}
            <div className="text-left">
              <p>{t('profile.theme')}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.themeSubtitle')}
              </p>
            </div>
          </span>
          <span className={`text-sm flex-shrink-0 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {themeLabel[theme]}
          </span>
        </button>

        <button 
          onClick={() => setShowLanguageModal(true)}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="language-selector-btn"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Globe className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('profile.language')}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.languageSubtitle')}
              </p>
            </div>
          </span>
          <span className={`text-sm flex items-center gap-2 flex-shrink-0 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            <span>{currentLanguage?.flag}</span>
            <span>{currentLanguage?.name}</span>
          </span>
        </button>

        {/* Country selector — used for the National Leaderboard */}
        <button
          onClick={() => setShowCountryModal(true)}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="country-selector-btn"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <MapPin className="w-5 h-5 opacity-70" />
            <span className="text-left">
              <span className="block">{t('profile.country')}</span>
              <span className={`block text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.countrySubtitle')}
              </span>
            </span>
          </span>
          <span className={`text-sm flex items-center gap-2 flex-shrink-0 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {user?.country ? (
              <>
                <span>{countryCodeToFlag(user.country)}</span>
                <span>{getCountryName(user.country, language)}</span>
              </>
            ) : (
              <span className={isDark ? 'text-white/40' : 'text-gray-400'}>
                {t('profile.countryNotSet')}
              </span>
            )}
          </span>
        </button>

        {/* Units & Format Settings */}
        <button 
          onClick={() => setShowUnitsModal(true)}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="units-settings-btn"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Scale className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('profile.unitsSettings')}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.unitsSettingsSubtitle')}
              </p>
            </div>
          </span>
          <ChevronRight className="w-5 h-5 opacity-70" />
        </button>

        {/* Weight visibility in challenges — when off, other participants
            only see kg lost & percentage, never the absolute weights. */}
        <button
          type="button"
          onClick={async () => {
            const next = !showWeightInChallenges;
            // Optimistic local update — the user's choice is authoritative
            // for the UI; if server sync fails we surface a toast but DO
            // NOT revert, so the toggle doesn't ping-pong on transient
            // network errors (the next mount will re-sync from server).
            setShowWeightInChallenges(next);
            localStorage.setItem('slimmy_show_weight', String(next));
            try {
              await httpClient.put(`${API_URL}/api/users/me/privacy-settings`, { show_weight: next });
            } catch (err) {
              const status = err?.response?.status;
              if (status === 400 || status === 404) {
                toast.error('Server non aggiornato. Ridistribuisci il backend.');
              } else {
                toast.error('Impossibile salvare. Riprova quando torni online.');
              }
            }
          }}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="weight-privacy-toggle"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Scale className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('profile.showWeightInChallenges') || 'Mostra peso nelle sfide'}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {showWeightInChallenges
                  ? (t('profile.showWeightInChallengesOn') || 'Visibile agli altri partecipanti')
                  : (t('profile.showWeightInChallengesOff') || 'Solo kg persi e percentuale')}
              </p>
            </div>
          </span>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors flex-shrink-0 ${
            showWeightInChallenges ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
          }`}>
            <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
              showWeightInChallenges ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </div>
        </button>

        {/* Microphone permission — visible to ALL users. Tapping when
            status='prompt' triggers a getUserMedia call inside the
            gesture, which shows the OS mic-permission dialog. Once
            granted the Walkie-Talkie button in the walk page becomes
            enabled. If denied the user must go to Settings → Slimmy
            → Microphone (we open a note explaining this). */}
        <button
          type="button"
          onClick={async () => {
            if (micPermission === 'granted') {
              toast.info(t('profile.micAlreadyGranted') || 'Microfono già abilitato. Puoi revocarlo dalle Impostazioni di sistema.');
              return;
            }
            if (micPermission === 'denied') {
              toast.error(t('profile.micDeniedGoSettings') || 'Microfono negato. Apri Impostazioni → Slimmy → Microfono per riabilitarlo.');
              return;
            }
            // 'prompt' or 'unsupported' → attempt getUserMedia from user gesture
            try {
              const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
              // Immediately stop — we only wanted the permission.
              stream.getTracks().forEach((t) => t.stop());
              setMicPermission('granted');
              localStorage.setItem('slimmy_mic_permission', 'granted');
              toast.success(t('profile.micGrantedToast') || 'Microfono abilitato per il Walkie-Talkie.');
            } catch (e) {
              const name = e?.name || '';
              if (name === 'NotAllowedError' || name === 'SecurityError') {
                setMicPermission('denied');
                localStorage.setItem('slimmy_mic_permission', 'denied');
                toast.error(t('profile.micDeniedGoSettings') || 'Microfono negato. Apri Impostazioni → Slimmy → Microfono per riabilitarlo.');
              } else {
                toast.error(t('profile.micErrorGeneric') || 'Impossibile abilitare il microfono.');
              }
            }
          }}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="mic-permission-toggle"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {micPermission === 'granted' ? (
              <Mic className="w-5 h-5 opacity-70 text-green-500" />
            ) : (
              <MicOff className="w-5 h-5 opacity-70" />
            )}
            <div className="text-left">
              <p>{t('profile.micPermission') || 'Microfono (Walkie-Talkie)'}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {micPermission === 'granted'
                  ? (t('profile.micGranted') || 'Attivo — Walkie-Talkie disponibile')
                  : micPermission === 'denied'
                    ? (t('profile.micDenied') || 'Negato — abilita dalle Impostazioni')
                    : (t('profile.micTapToEnable') || 'Tocca per abilitare')}
              </p>
            </div>
          </span>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors flex-shrink-0 ${
            micPermission === 'granted' ? 'bg-green-500' : isDark ? 'bg-white/10' : 'bg-gray-200'
          }`}>
            <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
              micPermission === 'granted' ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </div>
        </button>

        {/* Walk privacy — ADMIN ONLY. When on, starting a walk skips
            the "started workout" push to the user's challenge peers. */}
        {user?.is_admin && (
          <button
            type="button"
            onClick={async () => {
            const next = !walkPrivacyNoNotify;
            setWalkPrivacyNoNotify(next);
            localStorage.setItem('slimmy_walk_privacy', String(next));
            try {
              await updateUser({ walk_privacy_no_notify: next });
            } catch (err) {
              const status = err?.response?.status;
              if (status === 400 || status === 404) {
                toast.error('Server non aggiornato. Ridistribuisci il backend.');
              } else {
                toast.error('Impossibile salvare. Riprova quando torni online.');
              }
            }
          }}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="walk-privacy-toggle"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Bell className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('profile.walkPrivacyNoNotify') || 'Camminata in privato'}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {walkPrivacyNoNotify
                  ? (t('profile.walkPrivacyNoNotifyOn') || 'I partecipanti alle sfide non ricevono notifiche')
                  : (t('profile.walkPrivacyNoNotifyOff') || 'Notifica i partecipanti quando inizi una camminata')}
              </p>
            </div>
          </span>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors flex-shrink-0 ${
            walkPrivacyNoNotify ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
          }`}>
            <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
              walkPrivacyNoNotify ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </div>
          </button>
        )}

        {/* GLP-1 support — when on, Chef AI adapts recipes (lighter,
            high-protein, easily digestible portions) for users on
            weight-management therapies. Non-medical, always includes
            a disclaimer to follow doctor's indications. */}
        <button
          type="button"
          onClick={async () => {
            const next = !glp1Support;
            setGlp1Support(next);
            localStorage.setItem('slimmy_glp1_support', String(next));
            try {
              await updateUser({ glp1_support: next });
              await refreshUser();
            } catch (err) {
              setGlp1Support(!next);
              localStorage.setItem('slimmy_glp1_support', String(!next));
              toast.error('Impossibile salvare. Riprova quando torni online.');
            }
          }}
          className="w-full glass-card-sm p-4 flex items-center justify-between !border-2 !border-[#E8334A]"
          style={{ boxShadow: '0 0 0 3px rgba(232, 51, 74, 0.15)' }}
          data-testid="glp1-support-toggle"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Stethoscope className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('profile.glp1Support') || 'Supporto GLP-1'}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {glp1Support
                  ? (t('profile.glp1SupportOn') || 'Chef AI adatta le ricette per la tua terapia')
                  : (t('profile.glp1SupportOff') || 'Ricette adatte a chi segue una terapia GLP-1')}
              </p>
            </div>
          </span>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors flex-shrink-0 ${
            glp1Support ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
          }`}>
            <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
              glp1Support ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </div>
        </button>

        <button 
          onClick={() => navigate('/app/storico-pesi')}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Scale className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('weightEntry.history')}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.weightHistorySubtitle')}
              </p>
            </div>
          </span>
          <ChevronRight className="w-5 h-5 opacity-70" />
        </button>

        {/* Video Audio Toggle */}
        <button 
          onClick={() => {
            const newVal = !videoMuted;
            setVideoMuted(newVal);
            localStorage.setItem('slimmy_video_muted', String(newVal));
          }}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="video-audio-toggle"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {videoMuted 
              ? <VolumeX className="w-5 h-5 opacity-70" />
              : <Volume2 className="w-5 h-5 opacity-70" />
            }
            <div className="text-left">
              <p>{t('social.videoAudio') || 'Audio dei post'}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {videoMuted ? (t('social.videoAudioOff') || 'Disattivato') : (t('social.videoAudioOn') || 'Attivato')}
              </p>
            </div>
          </span>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
            !videoMuted ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
          }`}>
            <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
              !videoMuted ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </div>
        </button>

        {/* Workout Map Toggle */}
        <button 
          onClick={() => {
            const newVal = !showWorkoutMap;
            setShowWorkoutMap(newVal);
            localStorage.setItem('slimmy_workout_map', String(newVal));
          }}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="workout-map-toggle"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <MapPin className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('dashboard.workoutMap') || 'Mappa allenamenti'}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.workoutMapSubtitle')}
              </p>
            </div>
          </span>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
            showWorkoutMap ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
          }`}>
            <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
              showWorkoutMap ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </div>
        </button>

        {/* Blocked Users */}
        <button 
          onClick={() => navigate('/app/utenti-bloccati')}
          className="w-full glass-card-sm p-4 flex items-center justify-between"
          data-testid="blocked-users-btn"
        >
          <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <UserX className="w-5 h-5 opacity-70" />
            <div className="text-left">
              <p>{t('settings.blockedUsers') || 'Utenti Bloccati'}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.blockedUsersSubtitle')}
              </p>
            </div>
          </span>
          <ChevronRight className="w-5 h-5 opacity-70" />
        </button>

        {/* Bug report + Support — placed right before the logout CTA */}
        <BugReportButton />
        <SupportButton />

        <button 
          onClick={handleLogout}
          className="w-full glass-card-sm p-4 flex items-center justify-between text-[#E8334A]"
          data-testid="logout-btn"
        >
          <span className="flex items-center gap-3">
            <LogOut className="w-5 h-5" />
            {t('profile.logout')}
          </span>
        </button>
      </div>

      {/* Delete Account moved into the Settings (⚙️) bottom sheet. */}
      {/* Extra bottom spacing so the logout button doesn't sit flush
          against the bottom tab bar — gives the page breathing room. */}
      <div className="h-16" />
      </div>

      {/* Account Settings Bottom Sheet — opened from the ⚙️ icon in the
          top-right header. Contains only Account-level actions that don't
          require backend changes yet (Elimina account). When backend
          endpoints for change-email / change-phone will be ready, those
          will be added here. */}
      {showAccountSettings && (
        <div 
          className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowAccountSettings(false)}
          data-testid="account-settings-sheet"
        >
          <div 
            className={`w-full max-w-md rounded-t-3xl shadow-2xl animate-slide-up overflow-hidden ${
              isDark ? 'bg-[#1a1a1a]' : 'bg-white'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className={`w-12 h-1.5 rounded-full ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
            </div>
            
            {/* Header */}
            <div className="px-6 pt-3 pb-4">
              <h3 className={`font-display text-xl text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('profile.accountSettings') || 'Impostazioni Account'}
              </h3>
            </div>
            
            {/* Actions list */}
            <div className="px-4 pb-3 space-y-2">
              {/* Privacy Policy — opens in Capacitor in-app browser
                  (native overlay with built-in close button). */}
              <button
                type="button"
                data-testid="settings-privacy-btn"
                onClick={async () => {
                  setShowAccountSettings(false);
                  try {
                    const { Browser } = await import('@capacitor/browser');
                    if (!Browser?.open) throw new Error('Browser plugin not available');
                    await Browser.open({
                      url: `${window.location.origin}/privacy`,
                      presentationStyle: 'fullscreen',
                    });
                  } catch (err) {
                    console.warn('[Settings] Browser.open privacy failed, falling back:', err?.message);
                    window.open('/privacy', '_blank');
                  }
                }}
                className={`w-full p-4 rounded-2xl flex items-center justify-between transition-colors ${
                  isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <Shield className="w-5 h-5 opacity-70" />
                  <div className="text-left">
                    <p className="font-semibold">{t('legal.privacyPolicy') || 'Privacy Policy'}</p>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      {t('legal.privacyPolicySubtitle') || 'Come trattiamo i tuoi dati'}
                    </p>
                  </div>
                </span>
                <ChevronRight className="w-5 h-5 opacity-70" />
              </button>

              <button
                type="button"
                data-testid="settings-terms-btn"
                onClick={async () => {
                  setShowAccountSettings(false);
                  try {
                    const { Browser } = await import('@capacitor/browser');
                    if (!Browser?.open) throw new Error('Browser plugin not available');
                    await Browser.open({
                      url: `${window.location.origin}/terms`,
                      presentationStyle: 'fullscreen',
                    });
                  } catch (err) {
                    console.warn('[Settings] Browser.open terms failed, falling back:', err?.message);
                    window.open('/terms', '_blank');
                  }
                }}
                className={`w-full p-4 rounded-2xl flex items-center justify-between transition-colors ${
                  isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <BookOpen className="w-5 h-5 opacity-70" />
                  <div className="text-left">
                    <p className="font-semibold">{t('legal.termsOfService') || 'Termini di Servizio'}</p>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      {t('legal.termsOfServiceSubtitle') || 'Condizioni d\'uso del servizio'}
                    </p>
                  </div>
                </span>
                <ChevronRight className="w-5 h-5 opacity-70" />
              </button>

              <button
                type="button"
                data-testid="settings-delete-account-btn"
                onClick={() => {
                  setShowAccountSettings(false);
                  navigate('/app/elimina-account');
                }}
                className={`w-full p-4 rounded-2xl flex items-center justify-between transition-colors ${
                  isDark ? 'bg-red-500/10 hover:bg-red-500/15' : 'bg-red-50 hover:bg-red-100'
                }`}
              >
                <span className="flex items-center gap-3 text-red-500">
                  <Trash2 className="w-5 h-5" />
                  <div className="text-left">
                    <p className="font-semibold">{t('deleteAccount.title') || 'Elimina Account'}</p>
                    <p className={`text-xs ${isDark ? 'text-red-400/70' : 'text-red-500/70'}`}>
                      {t('deleteAccount.subtitle') || 'Eliminazione permanente di tutti i dati'}
                    </p>
                  </div>
                </span>
                <ChevronRight className="w-5 h-5 text-red-500" />
              </button>
            </div>
            
            {/* Cancel */}
            <div className="px-4 pt-2 pb-6">
              <button 
                type="button"
                onClick={() => setShowAccountSettings(false)}
                className={`w-full py-3.5 rounded-2xl font-semibold text-base transition-colors ${
                  isDark 
                    ? 'bg-white/10 text-white hover:bg-white/15' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('common.cancel') || 'Annulla'}
              </button>
            </div>
            
            <div className="pb-[env(safe-area-inset-bottom)]" />
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditProfileModal 
          user={user}
          isDark={isDark}
          t={t}
          onClose={() => setShowEditModal(false)}
          onSave={async (data) => {
            await updateUser(data);
            await refreshUser();
            setShowEditModal(false);
          }}
        />
      )}

      {/* Name & Bio Modal */}
      {showNameBioModal && (
        <NameBioModal 
          user={user}
          isDark={isDark}
          t={t}
          onClose={() => setShowNameBioModal(false)}
          onSave={async (data) => {
            await updateUser(data);
            await refreshUser();
            setShowNameBioModal(false);
          }}
        />
      )}

      {/* Theme Modal */}
      {showThemeModal && (
        <ThemeModal 
          isDark={isDark}
          currentTheme={theme}
          t={t}
          onSelect={(selectedTheme) => {
            setTheme(selectedTheme);
            setShowThemeModal(false);
          }}
          onClose={() => setShowThemeModal(false)}
        />
      )}

      {/* Language Modal */}
      {showLanguageModal && (
        <LanguageModal 
          isDark={isDark}
          currentLanguage={language}
          languages={languages}
          t={t}
          onSelect={(lang) => {
            setLanguage(lang);
            setShowLanguageModal(false);
          }}
          onClose={() => setShowLanguageModal(false)}
        />
      )}

      {/* Country Modal */}
      {showCountryModal && createPortal(
        <CountryModal
          isDark={isDark}
          currentCountry={user?.country}
          language={language}
          t={t}
          saving={savingCountry}
          onSelect={async (cc) => {
            try {
              setSavingCountry(true);
              await updateUser({ country: cc });
              await refreshUser();
              toast.success(t('profile.countrySaved'));
              setShowCountryModal(false);
            } catch (e) {
              toast.error(t('profile.countrySaveError'));
            } finally {
              setSavingCountry(false);
            }
          }}
          onClose={() => setShowCountryModal(false)}
        />,
        document.body
      )}

      {/* Units Settings Modal */}
      {showUnitsModal && createPortal(
        <UnitsSettingsModal 
          user={user}
          isDark={isDark}
          t={t}
          onClose={() => setShowUnitsModal(false)}
          onSave={async (data) => {
            await updateUser(data);
            await refreshUser();
            setShowUnitsModal(false);
          }}
        />,
        document.body
      )}

      {/* Health Integration Modal */}
      {showHealthModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowHealthModal(false)}
          />
          <div 
            className={`relative w-full max-w-md max-h-[85vh] overflow-y-auto ${
              isDark ? 'bg-[#0A0F1C]' : 'bg-white'
            } rounded-t-3xl sm:rounded-2xl animate-slide-up`}
            style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowHealthModal(false)}
              className={`absolute top-4 right-4 z-10 p-2 rounded-full ${
                isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
            <HealthIntegration onClose={() => setShowHealthModal(false)} />
          </div>
        </div>,
        document.body
      )}

      {/* Install PWA Modal */}
      <InstallPWAModal 
        isOpen={showInstallModal} 
        onClose={() => setShowInstallModal(false)} 
      />
    </div>
  );
}

function ProfilePhoto({ user, isDark, onPhotoUpdate }) {
  const fileInputRef = useRef(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Read EXIF orientation from image file
  const getExifOrientation = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const view = new DataView(e.target.result);
        if (view.getUint16(0, false) !== 0xFFD8) {
          resolve(1);
          return;
        }
        
        let offset = 2;
        while (offset < view.byteLength) {
          if (view.getUint16(offset, false) === 0xFFE1) {
            const exifOffset = offset + 4;
            if (view.getUint32(exifOffset, false) !== 0x45786966) {
              resolve(1);
              return;
            }
            
            const tiffOffset = exifOffset + 6;
            const littleEndian = view.getUint16(tiffOffset, false) === 0x4949;
            const ifdOffset = tiffOffset + view.getUint32(tiffOffset + 4, littleEndian);
            const entries = view.getUint16(ifdOffset, littleEndian);
            
            for (let i = 0; i < entries; i++) {
              const entryOffset = ifdOffset + 2 + i * 12;
              if (view.getUint16(entryOffset, littleEndian) === 0x0112) {
                resolve(view.getUint16(entryOffset + 8, littleEndian));
                return;
              }
            }
            resolve(1);
            return;
          }
          offset += 2 + view.getUint16(offset + 2, false);
        }
        resolve(1);
      };
      reader.readAsArrayBuffer(file.slice(0, 65536));
    });
  };

  // Process image with correct orientation using createImageBitmap for better browser support
  const processImageFile = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Use createImageBitmap which respects EXIF orientation natively
        const options = { imageOrientation: 'from-image' };
        
        let bitmap;
        try {
          bitmap = await createImageBitmap(file, options);
        } catch (e) {
          bitmap = await createImageBitmap(file);
        }
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        
        ctx.drawImage(bitmap, 0, 0);
        bitmap.close();
        
        const processedBase64 = canvas.toDataURL('image/jpeg', 0.90);
        resolve(processedBase64);
      } catch (error) {
        // Fallback to manual EXIF reading
        try {
          const result = await processImageWithManualExif(file);
          resolve(result);
        } catch (e) {
          reject(e);
        }
      }
    });
  };

  // Fallback: Manual EXIF reading for older browsers
  const processImageWithManualExif = (file) => {
    return new Promise(async (resolve) => {
      const orientation = await getExifOrientation(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (orientation >= 5 && orientation <= 8) {
            canvas.width = img.height;
            canvas.height = img.width;
          } else {
            canvas.width = img.width;
            canvas.height = img.height;
          }
          
          switch (orientation) {
            case 2: ctx.transform(-1, 0, 0, 1, canvas.width, 0); break;
            case 3: ctx.transform(-1, 0, 0, -1, canvas.width, canvas.height); break;
            case 4: ctx.transform(1, 0, 0, -1, 0, canvas.height); break;
            case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
            case 6: ctx.transform(0, 1, -1, 0, canvas.width, 0); break;
            case 7: ctx.transform(0, -1, -1, 0, canvas.width, canvas.height); break;
            case 8: ctx.transform(0, -1, 1, 0, 0, canvas.height); break;
            default: break;
          }
          
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.90));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const processedImage = await processImageFile(file);
        setSelectedImage(processedImage);
        setShowCropModal(true);
      } catch (err) {
        // Fallback
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedImage(e.target.result);
          setShowCropModal(true);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleSavePhoto = async (croppedImage) => {
    try {
      // Upload to object storage
      const res = await uploadProfilePhoto(croppedImage);
      await onPhotoUpdate();
      setShowCropModal(false);
      setSelectedImage(null);
    } catch (err) {
      console.error('Error saving photo:', err);
      alert('Errore durante il caricamento della foto');
    }
  };

  // Get picture URL - prioritize profile_photo (user uploaded) over picture (Google)
  const getPictureUrl = () => {
    // First check for user-uploaded profile_photo
    const photo = user?.profile_photo || user?.picture;
    if (!photo) return null;
    // If it's a storage path (starts with /api/files), use the backend URL
    if (photo.startsWith('/api/files/')) {
      return `${API_URL}${photo}`;
    }
    // Otherwise it's an external URL (Google avatar)
    return photo;
  };

  const pictureUrl = getPictureUrl();

  return (
    <>
      {/* Wrapper: leaves space for the floating "ADMIN" badge above
          the photo when the user is an admin. The badge is positioned
          absolutely so the photo stays centered like before. */}
      <div className="relative w-24 mx-auto mb-4">
        {user?.is_admin && (
          <span
            className="absolute left-1/2 -translate-x-1/2 -top-3 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest bg-[#E8334A] text-white shadow-md z-10 select-none"
            data-testid="admin-badge"
          >
            ADMIN
          </span>
        )}
        <div className={`relative w-24 h-24 mx-auto ${user?.is_admin ? 'ring-4 ring-[#E8334A] rounded-full p-0.5' : ''}`}>
          {pictureUrl ? (
            <img
              src={pictureUrl}
              alt={user.name}
              className="w-full h-full avatar cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            />
          ) : (
            <div
              className="w-full h-full avatar-placeholder text-3xl cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {user?.name?.[0]?.toUpperCase() || '?'}
            </div>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#E8334A] flex items-center justify-center shadow-lg"
            data-testid="change-photo-btn"
          >
            <Camera className="w-4 h-4 text-white" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="photo-input"
          />
        </div>
      </div>

      {showCropModal && selectedImage && (
        <PhotoCropModal 
          image={selectedImage}
          isDark={isDark}
          onSave={handleSavePhoto}
          onClose={() => {
            setShowCropModal(false);
            setSelectedImage(null);
          }}
        />
      )}
    </>
  );
}

function PhotoCropModal({ image, isDark, onSave, onClose }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [baseSize, setBaseSize] = useState({ width: 192, height: 192 });
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  
  const CONTAINER_SIZE = 192; // w-48 = 192px

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Calculate base image size to cover the circle at scale=1
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const imgAspect = img.naturalWidth / img.naturalHeight;
      let width, height;
      
      // Image must cover the circle at scale=1
      if (imgAspect > 1) {
        // Landscape: height = container, width = proportional
        height = CONTAINER_SIZE;
        width = CONTAINER_SIZE * imgAspect;
      } else {
        // Portrait/Square: width = container, height = proportional  
        width = CONTAINER_SIZE;
        height = CONTAINER_SIZE / imgAspect;
      }
      
      setBaseSize({ width, height });
    };
    img.src = image;
  }, [image]);

  // Calculate max drag limits based on current scale
  const getMaxDrag = () => {
    // At scale=1, max movement is (baseSize - container) / 2
    // At higher scales, more movement is allowed
    const scaledWidth = baseSize.width * scale;
    const scaledHeight = baseSize.height * scale;
    const maxX = Math.max(0, (scaledWidth - CONTAINER_SIZE) / 2);
    const maxY = Math.max(0, (scaledHeight - CONTAINER_SIZE) / 2);
    return { maxX, maxY };
  };

  // Handle drag start
  const handleDragStart = (clientX, clientY) => {
    setIsDragging(true);
    setDragStart({ x: clientX - position.x, y: clientY - position.y });
  };

  // Handle drag move - ensure image always covers the circle
  const handleDragMove = (clientX, clientY) => {
    if (!isDragging) return;
    
    const { maxX, maxY } = getMaxDrag();
    
    let newX = clientX - dragStart.x;
    let newY = clientY - dragStart.y;
    
    // Clamp position to keep image covering circle
    newX = Math.max(-maxX, Math.min(maxX, newX));
    newY = Math.max(-maxY, Math.min(maxY, newY));
    
    setPosition({ x: newX, y: newY });
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Reset position when scale changes - ensure image still covers circle
  useEffect(() => {
    const { maxX, maxY } = getMaxDrag();
    
    setPosition(prev => ({
      x: Math.max(-maxX, Math.min(maxX, prev.x)),
      y: Math.max(-maxY, Math.min(maxY, prev.y))
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, baseSize]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = imageRef.current;
      
      canvas.width = 200;
      canvas.height = 200;
      
      // Calculate scaled dimensions
      const scaledWidth = baseSize.width * scale;
      const scaledHeight = baseSize.height * scale;
      
      // Calculate what part of the original image is visible in the circle
      const scaleToNatural = img.naturalWidth / scaledWidth;
      const cropSize = CONTAINER_SIZE * scaleToNatural;
      
      // Center of visible area in display coordinates
      const visibleCenterX = (scaledWidth / 2) - position.x;
      const visibleCenterY = (scaledHeight / 2) - position.y;
      
      // Convert to natural image coordinates
      const offsetX = (visibleCenterX * scaleToNatural) - (cropSize / 2);
      const offsetY = (visibleCenterY * scaleToNatural) - (cropSize / 2);
      
      ctx.drawImage(
        img,
        offsetX, offsetY, cropSize, cropSize,
        0, 0, 200, 200
      );
      
      const croppedImage = canvas.toDataURL('image/jpeg', 0.8);
      await onSave(croppedImage);
    } finally {
      setSaving(false);
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-hidden"
      style={{ zIndex: 99999 }}
      onClick={onClose}
      onMouseMove={isDragging ? handleMouseMove : undefined}
      onMouseUp={isDragging ? handleMouseUp : undefined}
      onMouseLeave={isDragging ? handleMouseUp : undefined}
    >
      <div 
        className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Ritaglia foto
          </h3>
          <button onClick={onClose} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
            <X className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-gray-400'}`} />
          </button>
        </div>

        {/* Crop area with drag support */}
        <div 
          ref={containerRef}
          className="relative w-48 h-48 mx-auto mb-4 rounded-full overflow-hidden bg-black cursor-move select-none"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {baseSize.width > 0 && (
            <img 
              ref={imageRef}
              src={image}
              alt="Preview"
              className="absolute pointer-events-none"
              style={{ 
                // Use BASE dimensions (at scale=1), then apply CSS scale transform
                width: `${baseSize.width}px`,
                height: `${baseSize.height}px`,
                left: '50%',
                top: '50%',
                // Scale FIRST, then translate - this keeps drag movement consistent
                transform: `scale(${scale}) translate(calc(-50% + ${position.x / scale}px), calc(-50% + ${position.y / scale}px))`,
                transformOrigin: 'center center',
              }}
              draggable={false}
            />
          )}
          {/* Guide crosshair */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-0 right-0 h-px bg-white/20" />
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/20" />
          </div>
        </div>

        <p className={`text-xs text-center mb-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
          Trascina per posizionare
        </p>

        <div className="mb-6">
          <label className={`text-sm mb-2 block text-center ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            Zoom
          </label>
          <input 
            type="range"
            min="1"
            max="3"
            step="0.1"
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
            className="w-full"
          />
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex-1">
            {saving ? 'Salvo...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

function EditProfileModal({ user, isDark, onClose, onSave, t }) {
  // Calculate age from birth_date
  const calculateAge = (birthDate) => {
    if (!birthDate) return 30;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const [formData, setFormData] = useState({
    height_cm: user?.height_cm || 170,
    sex: user?.sex || '',
    age: calculateAge(user?.birth_date) || user?.age || 30,
    activity_level: user?.activity_level || 'moderate',
    weekly_goal: user?.weekly_goal || 0.5
  });
  const [saving, setSaving] = useState(false);

  // Simple scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentYear = new Date().getFullYear();
      const birthYear = currentYear - formData.age;
      const birthDate = `${birthYear}-01-01`;
      
      await onSave({
        height_cm: formData.height_cm ? parseInt(formData.height_cm) : null,
        sex: formData.sex || null,
        birth_date: birthDate,
        activity_level: formData.activity_level || null,
        weekly_goal: formData.weekly_goal ? parseFloat(formData.weekly_goal) : null
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Bottom Sheet */}
      <div 
        className={`absolute bottom-0 left-0 right-0 rounded-t-3xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'} animate-slide-up`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
        </div>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#E8334A] flex items-center justify-center shadow-lg"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Content */}
        <div className="px-5 pb-4">
          {/* Title */}
          <h2 className={`text-xl font-bold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('profile.personalInfo')}
          </h2>

          {/* Height & Age Pickers */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            {/* Height Picker */}
            <div>
              <label className={`text-xs font-medium uppercase tracking-wider mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.heightLabel')}
              </label>
              <WheelPicker
                values={Array.from({ length: 81 }, (_, i) => 140 + i)}
                selected={formData.height_cm}
                onChange={(val) => setFormData({...formData, height_cm: val})}
                suffix="cm"
                isDark={isDark}
              />
            </div>
            
            {/* Age Picker */}
            <div>
              <label className={`text-xs font-medium uppercase tracking-wider mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('profile.age') || 'Età'}
              </label>
              <WheelPicker
                values={Array.from({ length: 88 }, (_, i) => 13 + i)}
                selected={formData.age}
                onChange={(val) => setFormData({...formData, age: val})}
                suffix={t('profile.years') || 'anni'}
                isDark={isDark}
              />
            </div>
          </div>

          {/* Sex */}
          <div className="mb-5">
            <label className={`text-xs font-medium uppercase tracking-wider mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('profile.sex')}
            </label>
            <div className="flex gap-3">
              {[
                { value: 'male', label: t('profile.male') },
                { value: 'female', label: t('profile.female') }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFormData({...formData, sex: opt.value})}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium transition-colors ${
                    formData.sex === opt.value
                      ? 'bg-[#E8334A] text-white'
                      : isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Activity Level */}
          <div className="mb-5">
            <label className={`text-xs font-medium uppercase tracking-wider mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('profile.activityLevel')}
            </label>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                { value: 'sedentary', label: t('profile.sedentary') },
                { value: 'light', label: t('profile.light') },
                { value: 'moderate', label: t('profile.moderate') },
                { value: 'active', label: t('profile.active') },
                { value: 'very_active', label: t('profile.veryActive') }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFormData({...formData, activity_level: opt.value})}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    formData.activity_level === opt.value
                      ? 'bg-[#16A34A] text-white'
                      : isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Weekly Goal */}
          <div className="mb-6">
            <label className={`text-xs font-medium uppercase tracking-wider mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('profile.weeklyGoal')}
            </label>
            <div className="flex gap-2">
              {[0.25, 0.5, 0.75, 1.0].map(val => (
                <button
                  key={val}
                  onClick={() => setFormData({...formData, weekly_goal: val})}
                  className={`flex-1 py-3 rounded-xl text-center transition-colors ${
                    formData.weekly_goal === val
                      ? 'bg-[#F59E0B] text-white'
                      : isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="text-sm font-bold block">{val}</span>
                  <span className={`text-[10px] ${formData.weekly_goal === val ? 'text-white/80' : isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    kg/sett
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <button 
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 rounded-2xl text-white font-semibold text-base bg-gradient-to-r from-[#E8334A] to-[#F97316] disabled:opacity-50 shadow-lg"
          >
            {saving ? '...' : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wheel Picker Component
function WheelPicker({ values, selected, onChange, suffix, isDark }) {
  const scrollRef = useRef(null);
  const itemHeight = 44;
  const visibleItems = 3;
  const containerHeight = itemHeight * visibleItems;
  
  // Find index of selected value
  const selectedIndex = values.indexOf(selected);
  const initialIndex = selectedIndex >= 0 ? selectedIndex : Math.floor(values.length / 2);
  
  // Scroll to selected on mount
  useEffect(() => {
    if (scrollRef.current) {
      const scrollTop = initialIndex * itemHeight;
      scrollRef.current.scrollTop = scrollTop;
    }
  }, [initialIndex]);
  
  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop;
      const index = Math.round(scrollTop / itemHeight);
      const clampedIndex = Math.max(0, Math.min(index, values.length - 1));
      if (values[clampedIndex] !== selected) {
        onChange(values[clampedIndex]);
      }
    }
  };
  
  return (
    <div 
      className={`relative rounded-xl overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}
      style={{ height: containerHeight }}
    >
      {/* Selection highlight */}
      <div 
        className={`absolute left-0 right-0 pointer-events-none ${isDark ? 'bg-white/10' : 'bg-[#E8334A]/10'} border-y ${isDark ? 'border-white/20' : 'border-[#E8334A]/30'}`}
        style={{ top: itemHeight, height: itemHeight }}
      />
      
      {/* Scrollable list */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto scrollbar-hide snap-y snap-mandatory"
        style={{ scrollSnapType: 'y mandatory' }}
      >
        {/* Top padding */}
        <div style={{ height: itemHeight }} />
        
        {values.map((val, i) => {
          const isSelected = val === selected;
          return (
            <div
              key={val}
              className={`flex items-center justify-center snap-center transition-all ${
                isSelected 
                  ? isDark ? 'text-white font-bold' : 'text-[#E8334A] font-bold'
                  : isDark ? 'text-white/40' : 'text-gray-400'
              }`}
              style={{ height: itemHeight }}
              onClick={() => {
                onChange(val);
                if (scrollRef.current) {
                  scrollRef.current.scrollTo({
                    top: i * itemHeight,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              <span className={`text-lg ${isSelected ? 'scale-110' : 'scale-90'} transition-transform`}>
                {val}
              </span>
              {suffix && (
                <span className={`ml-1 text-sm ${isSelected ? '' : 'opacity-50'}`}>
                  {suffix}
                </span>
              )}
            </div>
          );
        })}
        
        {/* Bottom padding */}
        <div style={{ height: itemHeight }} />
      </div>
      
      {/* Gradient overlays */}
      <div className={`absolute top-0 left-0 right-0 h-10 pointer-events-none bg-gradient-to-b ${isDark ? 'from-[#1a1a2e]' : 'from-gray-100'} to-transparent`} />
      <div className={`absolute bottom-0 left-0 right-0 h-10 pointer-events-none bg-gradient-to-t ${isDark ? 'from-[#1a1a2e]' : 'from-gray-100'} to-transparent`} />
    </div>
  );
}

// Height Drum Picker Component - does NOT manage scroll lock (parent modal handles it)
function HeightPickerModal({ value, isDark, onSelect, onClose }) {
  // Ensure we have a valid initial value
  const initialValue = (typeof value === 'number' && value >= 140 && value <= 220) ? value : 170;
  const [selectedHeight, setSelectedHeight] = useState(initialValue);
  const scrollRef = useRef(null);
  const heights = Array.from({ length: 81 }, (_, i) => 140 + i); // 140-220 cm
  const itemHeight = 44;
  const containerHeight = 220;
  const paddingTop = (containerHeight - itemHeight) / 2; // 88px
  
  // Layout ref to store mounted state
  const layoutRef = useRef({ mounted: false });
  
  useEffect(() => {
    // Scroll to initial value
    const timer = setTimeout(() => {
      if (scrollRef.current && !layoutRef.current.mounted) {
        layoutRef.current.mounted = true;
        const index = heights.indexOf(initialValue);
        scrollRef.current.scrollTop = index * itemHeight;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [initialValue, heights]);

  const handleScroll = () => {
    if (scrollRef.current && layoutRef.current.mounted) {
      const scrollTop = scrollRef.current.scrollTop;
      const index = Math.round(scrollTop / itemHeight);
      const clampedIndex = Math.max(0, Math.min(index, heights.length - 1));
      const newHeight = heights[clampedIndex];
      if (newHeight !== selectedHeight) {
        setSelectedHeight(newHeight);
      }
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/70"
      style={{ zIndex: 100000 }}
      onClick={onClose}
    >
      <div 
        className={`w-64 rounded-2xl overflow-hidden ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`p-3 text-center border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Altezza (cm)</span>
        </div>
        
        <div className="relative" style={{ height: `${containerHeight}px` }}>
          {/* Selection indicator - centered vertically */}
          <div 
            className={`absolute left-4 right-4 rounded-xl pointer-events-none ${
              isDark ? 'bg-white/10' : 'bg-gray-100'
            }`}
            style={{ 
              top: `${paddingTop}px`,
              height: `${itemHeight}px` 
            }} 
          />
          
          {/* Scroll area */}
          <div 
            ref={scrollRef}
            data-testid="height-scroll"
            className="h-full overflow-y-auto hide-scrollbar"
            onScroll={handleScroll}
            style={{ 
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {/* Top padding so first item can be centered */}
            <div style={{ height: `${paddingTop}px` }} />
            
            {heights.map(h => (
              <div 
                key={h}
                className="flex items-center justify-center cursor-pointer"
                style={{ 
                  height: `${itemHeight}px`
                }}
                onClick={() => {
                  const index = heights.indexOf(h);
                  if (scrollRef.current) {
                    scrollRef.current.scrollTo({
                      top: index * itemHeight,
                      behavior: 'smooth'
                    });
                  }
                }}
              >
                <span className={`text-xl font-bold ${
                  h === selectedHeight 
                    ? 'text-[#E8334A]'
                    : isDark ? 'text-white/30' : 'text-gray-300'
                }`}>
                  {h}
                </span>
              </div>
            ))}
            
            {/* Bottom padding so last item can be centered */}
            <div style={{ height: `${paddingTop}px` }} />
          </div>
        </div>
        
        <div className={`p-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <button 
            onClick={() => onSelect(selectedHeight)}
            className="btn-primary w-full py-2.5 text-sm"
          >
            Conferma
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal dedicato per Nome, Nickname (username) e Bio
function NameBioModal({ user, isDark, onClose, onSave, t }) {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    username: user?.username || '',
    bio: user?.bio || ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const appShell = document.querySelector('.app-shell');
    if (appShell) appShell.style.overflow = 'hidden';
    return () => { 
      document.body.style.overflow = ''; 
      if (appShell) appShell.style.overflow = '';
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        name: formData.name.trim() || user?.name,
        username: formData.username.trim() || user?.username,
        bio: formData.bio
      });
      // onClose / setShowNameBioModal(false) is invoked by the
      // parent's onSave on success.
    } catch (err) {
      // Surface a toast so the user is not left wondering why
      // nothing happened. Most common failure modes:
      //   - 409 nickname already taken
      //   - 500 datetime serialization on legacy production users
      //   - network/offline
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : (t?.('common.errorGeneric') || 'Si è verificato un errore. Riprova.');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-3 py-2.5 rounded-xl text-sm ${
    isDark 
      ? 'bg-white/5 text-white border border-white/10 focus:border-[#E8334A]/50' 
      : 'bg-gray-50 text-gray-900 border border-gray-200 focus:border-[#E8334A]/50'
  } outline-none transition-colors`;

  const labelClass = `text-xs font-medium mb-1.5 block ${isDark ? 'text-white/60' : 'text-gray-500'}`;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-sm rounded-2xl p-5 animate-scale-in ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <h2 className={`font-display text-lg mb-5 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('profile.editProfile')}
        </h2>

        <div className="space-y-4">
          {/* Nome */}
          <div>
            <label className={labelClass}>{t('profile.name') || 'Nome'}</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder={t('profile.name') || 'Nome'}
              className={inputClass}
              maxLength={30}
              autoFocus
              data-testid="profile-edit-name"
            />
          </div>

          {/* Nickname (username) */}
          <div>
            <label className={labelClass}>Nickname</label>
            <input
              type="text"
              value={formData.username}
              onChange={e => setFormData({...formData, username: e.target.value.replace(/\s/g, '')})}
              placeholder="es. marco_fit"
              className={inputClass}
              maxLength={30}
              data-testid="profile-edit-nickname"
            />
            <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              {t('wizard.nicknameHint') || 'Visibile nel feed e nelle sfide'}
            </p>
          </div>

          {/* Bio */}
          <div>
            <label className={labelClass}>Bio</label>
            <textarea
              value={formData.bio}
              onChange={e => setFormData({...formData, bio: e.target.value})}
              className={`${inputClass} resize-none`}
              rows={3}
              maxLength={150}
            />
            <p className={`text-xs mt-1 text-right ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              {formData.bio.length}/150
            </p>
          </div>
        </div>

        {/* Bottoni azione */}
        <div className="flex gap-3 mt-5">
          <button 
            onClick={onClose} 
            className={`flex-1 py-3 rounded-xl text-sm font-medium transition-colors ${
              isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('common.cancel')}
          </button>
          <button 
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 rounded-xl text-sm font-medium bg-[#E8334A] text-white hover:bg-[#d62d42] transition-colors shadow-lg shadow-[#E8334A]/25"
          >
            {saving ? '...' : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ThemeModal({ isDark, currentTheme, onSelect, onClose, t }) {
  const themes = [
    { value: 'system', icon: Monitor, label: t('theme.system') },
    { value: 'light', icon: Sun, label: t('theme.light') },
    { value: 'dark', icon: Moon, label: t('theme.dark') }
  ];

  return (
    <div className="modal-bottom" onClick={onClose}>
      <div 
        className="modal-content animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className={`w-12 h-1 rounded-full mx-auto mb-6 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
        
        <h2 className={`font-display text-xl mb-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('theme.choose')}
        </h2>

        <div className="space-y-2 mb-6">
          {themes.map(theme => {
            const Icon = theme.icon;
            const isSelected = currentTheme === theme.value;
            return (
              <button
                key={theme.value}
                onClick={() => onSelect(theme.value)}
                className={`w-full p-4 rounded-xl flex items-center gap-3 transition-colors ${
                  isSelected 
                    ? 'bg-[#E8334A] text-white' 
                    : isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1 text-left">{theme.label}</span>
                {isSelected && <Check className="w-5 h-5" />}
              </button>
            );
          })}
        </div>

        <button onClick={onClose} className="btn-secondary w-full">
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}


function LanguageModal({ isDark, currentLanguage, languages, t, onSelect, onClose }) {
  return (
    <div className="modal-bottom" onClick={onClose}>
      <div 
        className="modal-content animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className={`w-12 h-1 rounded-full mx-auto mb-6 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
        
        <h2 className={`font-display text-xl mb-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('profile.chooseLanguage')}
        </h2>

        <div className="space-y-2 mb-6">
          {languages.map(lang => {
            const isSelected = currentLanguage === lang.code;
            return (
              <button
                key={lang.code}
                onClick={() => onSelect(lang.code)}
                data-testid={`lang-option-${lang.code}`}
                className={`w-full p-4 rounded-xl flex items-center gap-3 transition-colors ${
                  isSelected 
                    ? 'bg-[#E8334A] text-white' 
                    : isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                <span className="text-2xl">{lang.flag}</span>
                <span className="flex-1 text-left">{lang.name}</span>
                {isSelected && <Check className="w-5 h-5" />}
              </button>
            );
          })}
        </div>

        <button onClick={onClose} className="btn-secondary w-full">
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

// Country Picker Modal — used to set the user's country (drives the
// National Leaderboard). Names come localized from `Intl.DisplayNames`.
function CountryModal({ isDark, currentCountry, language, t, saving, onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const list = getCountryList(language);
  const filtered = search.trim()
    ? list.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : list;
  const selected = (currentCountry || '').toUpperCase();

  return (
    <div className="modal-bottom" onClick={onClose}>
      <div
        className="modal-content animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        style={{ height: 'min(640px, calc(100vh - 160px))', display: 'flex', flexDirection: 'column' }}
      >
        <div className={`w-12 h-1 rounded-full mx-auto mb-4 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />

        <h2 className={`font-display text-xl mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('profile.chooseCountry')}
        </h2>
        <p className={`text-xs text-center mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          {t('profile.countryModalHint')}
        </p>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('profile.countrySearchPlaceholder')}
          data-testid="country-search-input"
          className={`w-full mb-3 px-4 py-2 rounded-xl outline-none ${
            isDark
              ? 'bg-white/5 text-white placeholder:text-white/30 border border-white/10'
              : 'bg-gray-100 text-gray-900 placeholder:text-gray-400'
          }`}
        />

        <div className="space-y-2 overflow-y-auto pr-1" style={{ flex: 1 }}>
          {filtered.length === 0 && (
            <div className={`text-center py-8 text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              {t('profile.countryNoResults')}
            </div>
          )}
          {filtered.map((c) => {
            const isSel = selected === c.code;
            return (
              <button
                key={c.code}
                onClick={() => !saving && onSelect(c.code)}
                disabled={saving}
                data-testid={`country-option-${c.code}`}
                className={`w-full p-4 rounded-xl flex items-center gap-3 transition-colors disabled:opacity-50 ${
                  isSel
                    ? 'bg-[#E8334A] text-white'
                    : isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                <span className="text-2xl">{c.flag}</span>
                <span className="flex-1 text-left">{c.name}</span>
                {isSel && <Check className="w-5 h-5" />}
              </button>
            );
          })}
        </div>

        <button
          onClick={onClose}
          disabled={saving}
          className="btn-secondary w-full mt-4 disabled:opacity-50"
          data-testid="country-modal-close-btn"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}


// Units & Format Settings Modal
function UnitsSettingsModal({ user, isDark, t, onClose, onSave }) {
  const [formData, setFormData] = useState({
    date_format: user?.date_format || 'dmy',
    weight_unit: user?.weight_unit || 'kg',
    height_unit: user?.height_unit || 'cm',
    distance_unit: user?.distance_unit || 'km',
    decimal_separator: user?.decimal_separator || ','
  });

  // Ensure defaults are applied even if user fields are empty strings
  useEffect(() => {
    setFormData(prev => ({
      date_format: prev.date_format || 'dmy',
      weight_unit: prev.weight_unit || 'kg',
      height_unit: prev.height_unit || 'cm',
      distance_unit: prev.distance_unit || 'km',
      decimal_separator: prev.decimal_separator || ','
    }));
  }, []);
  const [saving, setSaving] = useState(false);

  // Lock scroll on mount - simplified for portal
  useEffect(() => {
    // Simply hide overflow on body
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const labelClass = `text-xs font-medium mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`;
  
  // Slightly lighter coral red for SELECTED options — visually distinct
  // from the darker primary "Save" button (#E8334A) to avoid confusion
  // between active selection and primary CTA.
  const selectedColor = 'bg-[#F25C71]';
  
  // Button for date format - smaller font to fit all 3 in one row
  const DateOptionButton = ({ selected, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={`py-3 px-2 rounded-xl text-xs font-medium transition-all flex-1 whitespace-nowrap ${
        selected
          ? `${selectedColor} text-white`
          : isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {children}
    </button>
  );
  
  // Normal button for other options
  const OptionButton = ({ selected, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={`py-3 px-4 rounded-xl text-sm font-medium transition-all flex-1 ${
        selected
          ? `${selectedColor} text-white`
          : isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="modal-bottom" onClick={onClose}>
      <div 
        className="modal-content animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className={`w-10 h-1 rounded-full mx-auto mb-4 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
        
        {/* Title */}
        <h2 className={`font-display text-lg mb-4 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('profile.unitsSettings')}
        </h2>

        <div className="space-y-5">
          {/* Date Format */}
          <div>
            <label className={labelClass}>{t('profile.dateFormat')}</label>
            <div className="grid grid-cols-3 gap-2">
              <DateOptionButton 
                selected={formData.date_format === 'dmy'}
                onClick={() => setFormData({...formData, date_format: 'dmy'})}
              >
                {t('profile.dateFormatDMY')}
              </DateOptionButton>
              <DateOptionButton 
                selected={formData.date_format === 'mdy'}
                onClick={() => setFormData({...formData, date_format: 'mdy'})}
              >
                {t('profile.dateFormatMDY')}
              </DateOptionButton>
              <DateOptionButton 
                selected={formData.date_format === 'ymd'}
                onClick={() => setFormData({...formData, date_format: 'ymd'})}
              >
                {t('profile.dateFormatYMD')}
              </DateOptionButton>
            </div>
          </div>

          {/* Weight & Height Units */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('profile.weightUnit')}</label>
              <div className="flex gap-2">
                <OptionButton 
                  selected={formData.weight_unit === 'kg'}
                  onClick={() => setFormData({...formData, weight_unit: 'kg'})}
                >
                  kg
                </OptionButton>
                <OptionButton 
                  selected={formData.weight_unit === 'lbs'}
                  onClick={() => setFormData({...formData, weight_unit: 'lbs'})}
                >
                  lbs
                </OptionButton>
              </div>
            </div>
            <div>
              <label className={labelClass}>{t('profile.heightUnit')}</label>
              <div className="flex gap-2">
                <OptionButton 
                  selected={formData.height_unit === 'cm'}
                  onClick={() => setFormData({...formData, height_unit: 'cm'})}
                >
                  cm
                </OptionButton>
                <OptionButton 
                  selected={formData.height_unit === 'ft'}
                  onClick={() => setFormData({...formData, height_unit: 'ft'})}
                >
                  ft
                </OptionButton>
              </div>
            </div>
          </div>

          {/* Distance & Decimal */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('profile.distanceUnit')}</label>
              <div className="flex gap-2">
                <OptionButton 
                  selected={formData.distance_unit === 'km'}
                  onClick={() => setFormData({...formData, distance_unit: 'km'})}
                >
                  km
                </OptionButton>
                <OptionButton 
                  selected={formData.distance_unit === 'mi'}
                  onClick={() => setFormData({...formData, distance_unit: 'mi'})}
                >
                  mi
                </OptionButton>
              </div>
            </div>
            <div>
              <label className={labelClass}>{t('profile.decimalSeparator')}</label>
              <div className="flex gap-2">
                <OptionButton 
                  selected={formData.decimal_separator === ','}
                  onClick={() => setFormData({...formData, decimal_separator: ','})}
                >
                  1,5
                </OptionButton>
                <OptionButton 
                  selected={formData.decimal_separator === '.'}
                  onClick={() => setFormData({...formData, decimal_separator: '.'})}
                >
                  1.5
                </OptionButton>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1 py-3">
            {t('common.cancel')}
          </button>
          <button 
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex-1 py-3"
          >
            {saving ? '...' : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
